import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Code2, Palette, ArrowRight } from 'lucide-react';
import { isFinnishPage } from '../../lib/blogPaths';

const HOME_SERVICES_EN = [
  {
    icon: Code2,
    title: 'Software Development',
    description: 'Full-stack developers—individuals and teams—that integrate seamlessly with yours.',
    href: '/services/software-development',
    cta: 'Learn More',
  },
  {
    icon: Palette,
    title: 'UI/UX Design',
    description: 'User-centered design that drives engagement and results.',
    href: '/services/ui-ux-design',
    cta: 'Learn More',
  },
];

const HOME_SERVICES_FI = [
  {
    icon: Code2,
    title: 'Ohjelmistokehitys',
    description: 'Full-stack-kehittäjiä—yksilöitä ja tiimejä—jotka integroituvat saumattomasti omaan tiimiisi.',
    href: '/palvelut/ohjelmistokehitys',
    cta: 'Lue lisää',
  },
  {
    icon: Palette,
    title: 'UI/UX-suunnittelu',
    description: 'Käyttäjäkeskeinen suunnittelu, joka lisää sitoutumista ja tuloksia.',
    href: '/palvelut/ui-ux-suunnittelu',
    cta: 'Lue lisää',
  },
];

const SERVICES_PAGE_SERVICES = [
  {
    icon: Code2,
    title: 'Software Development',
    description:
      'Full-stack developers—individuals and teams—that integrate with yours. From web and mobile apps to complex integrations—all powered by AI tools for faster, higher-quality delivery.',
    listTitle: 'What we build',
    items: [
      { label: 'Web Applications', detail: 'React, Next.js, Node.js' },
      { label: 'Mobile Apps', detail: 'React Native, Flutter' },
      { label: 'APIs & Integrations', detail: 'RESTful, GraphQL' },
      { label: 'Cloud Infrastructure', detail: 'AWS, Azure, DevOps' },
      { label: 'Architecture', detail: 'System design, scalability' },
    ],
    href: '/services/software-development',
    cta: 'Learn more',
  },
  {
    icon: Palette,
    title: 'UI/UX Design',
    description:
      'User-centered design that drives engagement and conversion. From user research to final UI—we ensure your product delights users and achieves business goals.',
    listTitle: 'Capabilities',
    items: [
      { label: 'User Research', detail: 'Interviews, testing, personas' },
      { label: 'Interface Design', detail: 'Figma, design systems' },
      { label: 'Prototyping', detail: 'Interactive prototypes, iteration' },
      { label: 'Design Ops', detail: 'Handoff, component libraries' },
    ],
    href: '/services/ui-ux-design',
    cta: 'Learn more',
  },
];

const SERVICES_PAGE_SERVICES_FI = [
  {
    icon: Code2,
    title: 'Ohjelmistokehitys',
    description:
      'Tuomme ohjelmistokehitykseen laatua, ajantasaista osaamista ja alan parhaita käytäntöjä. Tarjoamme konsultointia ja osaamista verkko- ja mobiilisovellusten kehitykseen, räätälöityihin ratkaisuihin ja integraatioihin.',
    listTitle: 'Mitä rakennamme',
    items: [
      { label: 'Verkkosovellukset', detail: 'React, Next.js, Node.js' },
      { label: 'Mobiilisovellukset', detail: 'React Native, Flutter' },
      { label: 'Rajapinnat ja integraatiot', detail: 'RESTful, GraphQL' },
      { label: 'Pilvi-infrastruktuuri', detail: 'AWS, Azure, DevOps' },
      { label: 'Arkkitehtuuri', detail: 'Järjestelmäsuunnittelu, skaalautuvuus' },
    ],
    href: '/palvelut/ohjelmistokehitys',
    cta: 'Lue lisää',
  },
  {
    icon: Palette,
    title: 'UI & UX Design',
    description:
      'Nykyaikaisessa sovelluskehityksessä loppukäyttäjän kokemus ratkaisee. Taitavat suunnittelijamme varmistavat palvelumuotoilun sekä käyttöliittymä- ja käyttäjäkokemussuunnittelun avulla palvelunne käyttäjäystävällisyyden ja houkuttelevuuden.',
    listTitle: 'Osaamisalueet',
    items: [
      { label: 'Käyttäjätutkimus', detail: 'Haastattelut, testaus, personat' },
      { label: 'Käyttöliittymäsuunnittelu', detail: 'Figma, design systemit' },
      { label: 'Prototypointi', detail: 'Interaktiiviset prototyypit, iterointi' },
      { label: 'Design Ops', detail: 'Handoff, komponenttikirjastot' },
    ],
    href: '/palvelut/ui-ux-suunnittelu',
    cta: 'Lue lisää',
  },
];

export function ServicesSection({
  variant = 'home',
  /** When variant is services, override section heading (from ACF). */
  servicesHeading: servicesHeadingProp,
  /** When variant is services, override section subtitle (from ACF). */
  servicesSubtitle: servicesSubtitleProp,
}) {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: variant === 'home' ? '-100px' : '-80px' });
  const isFinnish = variant === 'home' && isFinnishPage(router?.pathname);
  const isServicesPageFinnish = variant === 'services' && isFinnishPage(router?.pathname);
  const services =
    variant === 'services'
      ? isServicesPageFinnish
        ? SERVICES_PAGE_SERVICES_FI
        : SERVICES_PAGE_SERVICES
      : isFinnish
        ? HOME_SERVICES_FI
        : HOME_SERVICES_EN;
  const isHome = variant === 'home';

  const homeHeading = isFinnish ? 'Mitä teemme' : 'What We Do';
  const homeSubtitle = isFinnish ? 'Syvällistä osaamista ohjelmistokehityksessä ja suunnittelussa' : 'Two core offerings, infinite possibilities';
  const servicesHeadingDefault = isServicesPageFinnish ? 'Palvelumme' : 'Our Services';
  const servicesSubtitleDefault = isServicesPageFinnish
    ? 'Konsultointiosaamisemme kattaa modernin ohjelmistokehityksen koko kirjon.'
    : 'Our consulting expertise spans the full spectrum of modern software development.';
  const servicesHeading = servicesHeadingProp ?? servicesHeadingDefault;
  const servicesSubtitle = servicesSubtitleProp ?? servicesSubtitleDefault;
  const servicesNote = isServicesPageFinnish
    ? 'Tämä on vain osa teknologioistamme. Kerro meille tarpeesi, niin löydämme parhaan ratkaisun projektiisi.'
    : "This is just a selection of our technologies. Share your needs and we'll find the best solution for your project.";

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">
        {isHome && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="heading-2 mb-4">{homeHeading}</h2>
            <p className="text-large text-[#6b7280]">
              {homeSubtitle}
            </p>
          </motion.div>
        )}

        {variant === 'services' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <h2 className="heading-2 mb-4">{servicesHeading}</h2>
            <p className="text-large text-[#6b7280] max-w-2xl mx-auto">
              {servicesSubtitle}
            </p>
          </motion.div>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isHome ? 'mb-0' : 'mb-10'}`}>
          {services.map((service, index) => {
            const Icon = service.icon;
            if (isHome) {
              return (
                <Link key={service.title} href={service.href} className="block group">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    className="h-full p-8 md:p-10 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.12)] hover:scale-[1.02] transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-[#0f172a] rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="heading-3 mb-2 text-gray-900">{service.title}</h3>
                    <p className="text-base text-[#6b7280] mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <span className="text-brand font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                      {service.cta}
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  </motion.div>
                </Link>
              );
            }
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-2xl border border-gray-200 hover:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.12)] hover:scale-[1.02] transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#0f172a] rounded-xl flex items-center justify-center mb-6">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="heading-3 mb-2">{service.title}</h3>
                <p className="text-base text-[#6b7280] mb-6 leading-relaxed">
                  {service.description}
                </p>
                {service.items && (
                  <>
                    {service.listTitle && (
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">{service.listTitle}</h4>
                    )}
                    <ul className="space-y-2 mb-8 text-gray-500">
                      {service.items.map((item) => (
                        <li key={item.label} className="flex flex-wrap gap-1">
                          <strong className="text-gray-700">{item.label}</strong>
                          <span>— {item.detail}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {service.href ? (
                  <Link
                    href={service.href}
                    className="text-brand font-medium flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    {service.cta}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="text-brand font-medium flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    {service.cta}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {variant === 'services' && (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center text-lg text-[#6b7280] max-w-2xl mx-auto"
          >
            {servicesNote}
          </motion.p>
        )}
      </div>
    </section>
  );
}
